/* eslint-disable import/no-extraneous-dependencies */
import { useState, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  styled,
  useTheme
} from '@material-ui/core';
import ExpandMoreRoundedIcon from '@material-ui/icons/ExpandMoreRounded';

import { SButton, SSelectDropdown, SText } from '@setvi/shared/components';
import { useDialog } from '@setvi/shared/providers';
import { DownloadIconV2, ThickIconV2 } from '@setvi/shared/components/sicons';

import {
  generateRfqCSVQuery,
  getExportedQuoteMutation,
  getExportedRFQMutation
} from 'Services';
import { downloadFile } from 'utilsV2';
import {
  AnalyticsModules,
  DBEventType,
  useAnalytics,
  useDatabricksAnalytics
} from '@setvi/shared/hooks';
import { useRFQStore } from 'pages/auto-rfq/store';
import { useQuoteStore } from 'pages/quotes/store';
import { useRfqId } from 'pages/auto-rfq/context/rfq-id';
import { useRfqConfigStore } from 'pages/auto-rfq/store/rfqConfig';
import { parseProcessingTimeToSeconds } from 'pages/auto-rfq/utils';
import { useTooltipStyles } from '../quote-details-modal/style';
import ExportWarningDialog from './export-warning-dialog';

const EXPORT_FAILED_DIALOG = 'auto-rfq-export-failed-confirm';

export const StyledItem = styled(ListItem)({
  padding: '5px',
  borderRadius: 36,
  transition: 'all 0.3s ease',

  '& .MuiListItemIcon-root': {
    visibility: 'hidden'
  },

  '&:hover': {
    '& .MuiListItemIcon-root': {
      visibility: 'visible'
    }
  }
});

const ExportMenu = ({
  files
}: {
  files: { id: string; name: string; statusId?: string }[];
}) => {
  const id = useRfqId();
  const { palette } = useTheme();
  const { openDialog, closeDialog } = useDialog();
  const { enqueueSnackbar } = useSnackbar();
  const { captureAnalytics } = useAnalytics();
  const tooltipClasses = useTooltipStyles();
  const { captureDBAnalytics } = useDatabricksAnalytics();

  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  const {
    name,
    quoteId,
    analytics,
    competitor,
    files: allFiles,
    currentJobId,
    isSocketActive,
    processing,
    uploading
  } = useRFQStore();
  const { quote } = useQuoteStore();
  const { rfqToQuoteEnabled } = useRfqConfigStore();

  const { mutateAsync: getExport, isLoading } = useMutation(
    getExportedRFQMutation()
  );

  const useOldExport = files?.every(file => !file?.statusId) ?? true;

  // Old logic for RFQ Export
  const { isFetching: isCsvFetching, refetch } = useQuery({
    ...generateRfqCSVQuery(id),
    enabled: false
  });

  // Logic for Quote export
  const { mutateAsync: getQuoteExport, isLoading: isQuoteExportLoading } =
    useMutation(getExportedQuoteMutation());

  const fileRows = useMemo(
    () =>
      allFiles?.map(f => ({
        name: f?.file?.name || 'Untitled file',
        failed: f?.status === 'FAILED'
      })) ?? [],
    [allFiles]
  );
  const hasFailedFiles = fileRows.some(f => f.failed);
  const hasCompletedFiles = useMemo(
    () =>
      files?.some(f => {
        const sf = allFiles.find(s => s.id === f.id);
        return sf?.status !== 'FAILED' && sf?.file?.total > 0;
      }) ?? false,
    [files, allFiles]
  );

  const runExport = async (
    exportFileMode: 'AugmentedOriginal' | 'RfqFormat',
    includeFailed: boolean
  ) => {
    const filesToExport =
      files?.filter(f => {
        const storeFile = allFiles.find(sf => sf.id === f.id);
        // Failed files are exported only when the user explicitly opts in.
        if (storeFile?.status === 'FAILED') return includeFailed;
        // Completed files are exported when they have items.
        return storeFile?.file?.total > 0;
      }) ?? [];

    let success = 0;
    let failed = 0;

    await Promise.all(
      filesToExport.map(async i => {
        try {
          if (!i?.id) return;
          const start = Date.now();

          const res = await getExport({
            statusId: i?.id,
            exportFileMode
          });

          const durationInSeconds = (Date.now() - start) / 1000;

          const ok = await downloadFile(
            res,
            `${i?.name || 'File'}_${exportFileMode === 'RfqFormat' ? 'SETVI_RFQ_Format' : 'Original'}.xlsx`
          );

          if (ok) success += 1;
          else failed += 1;

          captureAnalytics(
            {
              action: 'export',
              subAction: exportFileMode,
              ...(!!currentJobId && {
                jobId: currentJobId
              }),
              ...(!!durationInSeconds && {
                apiTime: durationInSeconds
              }),
              filestackUrl: allFiles.find(f => f.id === i?.id)?.file.url || '',
              processed: analytics?.processed || 0,
              matched: analytics?.matched || 0,
              mfgSkuMatched: analytics?.mfgMatched || 0,
              matchedFoundOnline: analytics?.onlineMatched || 0,
              nonMatched: analytics?.notMatched || 0,
              nonMfgSkuMatched: analytics?.nonMfgMatched || 0,
              processingTime: analytics?.fileProcessingTime || 0,
              privateLabelMatched: analytics?.privateLabelMatched || 0,
              privateLabelSwapped: analytics?.privateLabelSwapped || 0,
              ...(!!competitor && {
                competitorId: competitor?._id || ''
              })
            },
            AnalyticsModules.AutoRFQCreate
          );
        } catch (err) {
          failed += 1;
          captureAnalytics(
            {
              action: 'export',
              subAction: exportFileMode,
              ...(!!currentJobId && {
                jobId: currentJobId
              }),
              filestackUrl: allFiles.find(f => f.id === i?.id)?.file.url || '',
              ...(!!competitor && {
                competitorId: competitor?._id || ''
              }),
              error: err?.message || err || ''
            },
            AnalyticsModules.AutoRFQCreate
          );
        }
      })
    );

    if (success > 0 && failed === 0) {
      enqueueSnackbar(
        `Exported ${success} file${success > 1 ? 's' : ''} successfully`,
        { variant: 'success' }
      );
    } else if (success > 0 && failed > 0) {
      enqueueSnackbar(
        `Exported ${success} file${success > 1 ? 's' : ''}, ${failed} failed`,
        { variant: 'warning' }
      );
    } else if (failed > 0) {
      enqueueSnackbar('Failed to export files', {
        variant: 'error',
        autoHideDuration: 8000
      });
    }
  };

  const handleExport = (exportFileMode: 'AugmentedOriginal' | 'RfqFormat') => {
    if (hasFailedFiles) {
      // Close the Download dropdown so it doesn't float over the modal backdrop.
      setAnchor(null);
      openDialog(
        <ExportWarningDialog
          files={fileRows}
          hasCompletedFiles={hasCompletedFiles}
          onExport={async includeFailed => {
            await runExport(exportFileMode, includeFailed);
            closeDialog(EXPORT_FAILED_DIALOG);
          }}
          onClose={() => closeDialog(EXPORT_FAILED_DIALOG)}
        />,
        { id: EXPORT_FAILED_DIALOG }
      );
      return;
    }
    runExport(exportFileMode, false);
  };

  const handleQuoteExport = async () => {
    if (!quoteId) return;
    enqueueSnackbar(
      'Please wait, Your file is being prepared. This may take 2–3 minutes. Please keep this window open.',
      {
        variant: 'info'
      }
    );

    const res = await getQuoteExport({ quoteId });
    const success = await downloadFile(res, `quote-${name}.pdf`);

    // Capture Databricks Analytics
    captureDBAnalytics({
      eventType: DBEventType.QuotesModule,
      data: {
        rfqId: id,
        quoteId,
        action: 'downloadQuotePDF',
        ...(res?.error && { error: res?.error }),
        source: {
          name: 'RFQDetails'
        }
      }
    });

    const msg = success
      ? 'Your file is now ready. The download will begin automatically.'
      : 'Error while downloading quote PDF';

    enqueueSnackbar(msg, {
      variant: success ? 'success' : 'error'
    });
  };

  const options = [
    {
      name: 'Original File + Product Match',
      id: 'original',
      onClick: () => handleExport('AugmentedOriginal')
    },
    {
      name: 'SETVI RFQ Format',
      id: 'setvi-format',
      onClick: () => handleExport('RfqFormat')
    },
    ...(quoteId && rfqToQuoteEnabled
      ? [
          {
            name: 'Quote PDF',
            id: 'quote-pdf',
            onClick: () => handleQuoteExport()
          }
        ]
      : [])
  ];

  const handleOldExport = async (csvName: string) => {
    const start = Date.now();

    const res = await refetch();
    const durationInSeconds = (Date.now() - start) / 1000;

    captureAnalytics(
      {
        ...(res?.error && { error: res?.error }),
        action: 'export',
        id,
        ...(!!durationInSeconds && {
          apiTime: durationInSeconds
        }),
        processed: analytics?.processed || 0,
        matched: analytics?.matched || 0,
        mfgSkuMatched: analytics?.mfgMatched || 0,
        matchedFoundOnline: analytics?.onlineMatched || 0,
        nonMatched: analytics?.notMatched || 0,
        nonMfgSkuMatched: analytics?.nonMfgMatched || 0,
        processingTime: parseProcessingTimeToSeconds(analytics?.time),
        ...(!!competitor && {
          competitorId: competitor?._id || ''
        })
      },
      AnalyticsModules.AutoRFQList
    );

    if (!res?.data) return;

    const success = await downloadFile(res?.data, csvName);

    if (success) {
      enqueueSnackbar('RFQ exported successfully', {
        variant: 'success'
      });
    }
  };

  const showWarning = useMemo(
    () =>
      !quote?.previewInfo?.preparedBy?.email ||
      !quote?.previewInfo?.preparedBy?.firstName ||
      !quote?.previewInfo?.preparedBy?.lastName ||
      !quote?.previewInfo?.preparedBy?.phoneNumber ||
      !quote?.previewInfo?.preparedFor?.companyAddress ||
      !quote?.previewInfo?.preparedFor?.companyName ||
      !quote?.previewInfo?.preparedFor?.companyPhoneNumber,
    [quote]
  );

  if (useOldExport)
    return (
      <SButton
        style={{
          borderRadius: 36
        }}
        disabled={
          isSocketActive ||
          isCsvFetching ||
          processing ||
          uploading ||
          !analytics?.processed
        }
        onClick={() => handleOldExport(name || 'Rfq_Export')}
        loading={isCsvFetching}
        startIcon={<DownloadIconV2 fill="primary" />}>
        Download
      </SButton>
    );

  return (
    <SSelectDropdown
      width={245}
      newDesign
      placement="bottom-end"
      anchorElement={anchor}
      onClose={() => setAnchor(null)}
      anchor={
        <SButton
          style={{
            borderRadius: 36
          }}
          loading={isLoading}
          disabled={
            !files?.length ||
            isSocketActive ||
            processing ||
            uploading ||
            !analytics?.processed
          }
          onClick={e => setAnchor(e.currentTarget)}
          startIcon={<DownloadIconV2 fill="primary" />}
          endIcon={
            <ExpandMoreRoundedIcon
              style={{
                transition: 'all 0.3s ease',
                rotate: anchor ? '180deg' : '0deg'
              }}
            />
          }>
          Download
        </SButton>
      }
      content={
        <Box overflow="auto" maxHeight={220}>
          <List
            style={{
              width: '100%',
              padding: 0
            }}>
            {options.map(option => {
              const isQuotePdf = option.id === 'quote-pdf';
              const shouldShowTooltip = isQuotePdf && showWarning;
              const isDisabled = isQuotePdf
                ? isQuoteExportLoading || showWarning
                : isLoading;

              return (
                <Tooltip
                  key={option.id}
                  arrow
                  placement="left-end"
                  classes={tooltipClasses}
                  title={
                    shouldShowTooltip
                      ? 'Please fill in all required information before downloading the file'
                      : ''
                  }
                  disableHoverListener={!shouldShowTooltip}>
                  <span>
                    <StyledItem
                      button
                      onClick={option?.onClick}
                      disabled={isDisabled}>
                      <ListItemText
                        primary={
                          <Box
                            display="flex"
                            alignItems="center"
                            gridGap={6}
                            onClick={() => {}}>
                            <SText size="sm">{option?.name}</SText>
                          </Box>
                        }
                      />

                      <ListItemIcon
                        style={{
                          display: 'flex',
                          justifyContent: 'end',
                          minWidth: 'auto'
                        }}>
                        <ThickIconV2 fill={palette.primary.main} />
                      </ListItemIcon>
                    </StyledItem>
                  </span>
                </Tooltip>
              );
            })}
          </List>
        </Box>
      }
    />
  );
};

export default ExportMenu;
